import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import useTabMusic from "@/hooks/useTabMusic";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Library, Search, Sparkles, Loader2, Heart, Sword, Shield, Zap, X, Flame, Handshake, Languages, Swords } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { resolveImageUrl } from "@/utils/imageUrl";
import { t } from "@/utils/i18n";
import PveDeckBuilder from "@/components/game/PveDeckBuilder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RARITIES = ["legendary", "epic", "rare", "uncommon", "common"];
const RARITY_COLOR = {
  common: "#94a3b8",
  uncommon: "#22c55e",
  rare: "#3b82f6",
  epic: "#a855f7",
  legendary: "#f59e0b",
};

function TinyStat({ Icon, value, color }) {
  if (!value && value !== 0) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px]" style={{ color }}>
      <Icon size={10} />{value}
    </span>
  );
}

function CardModal({ entry, lang, onClose, onTrade, onTranslated }) {
  const { game } = useGame();
  const { token, API } = useAuth();
  const [translating, setTranslating] = useState(false);
  if (!entry) return null;
  const color = RARITY_COLOR[entry.rarity] || "#94a3b8";
  const isIt = lang.startsWith("it");
  // Map card type → inventory category used by trade endpoint
  const tradeCategory = entry.type === "ally" ? "allies" : entry.type === "ability" ? "abilities" : "objects";

  const isEnglish = (lang || "en").startsWith("en");
  const alreadyTranslated = !!(entry._translations && entry._translations[(lang || "en").slice(0, 2)]);
  const canTranslate = entry.currently_owned && !isEnglish && !alreadyTranslated;

  const handleTranslate = async () => {
    if (!entry || !game?.id) return;
    setTranslating(true);
    try {
      const res = await axios.post(
        `${API}/game/${game.id}/card/translate`,
        { card_id: entry.id, card_category: tradeCategory },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
      );
      if (res.data?.translated) {
        toast.success(isIt ? "Carta tradotta" : "Card translated");
        onTranslated?.(res.data.card);
      } else {
        toast.info(isIt ? "Nessuna traduzione necessaria" : "No translation needed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (isIt ? "Traduzione fallita" : "Translation failed"));
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="collection-card-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="relative max-w-sm w-full my-auto max-h-[calc(100dvh-1.5rem)] overflow-y-auto"
      >
        <div className="relative">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 z-20 bg-black/80 hover:bg-[#8b0000]/80 border border-[#cfb53b]/60 rounded-full w-9 h-9 flex items-center justify-center text-[#cfb53b] shadow-lg transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          
          <div className="mtg-card-wrapper animate-fadeInUp">
            {/* Visual MTG Card */}
            <div className={`mtg-card card-${entry.type === "ally" ? "ally" : entry.type === "ability" ? "ability" : "object"} rarity-${(entry.rarity || "common").toLowerCase()}`}>
              <div className={`mtg-inner-frame ${entry.foil ? "card-foil" : ""}`}>
                {/* Header Bar */}
                <div className="mtg-header-bar">
                  <span className="mtg-card-title">{entry.name}</span>
                  <div className="mtg-header-right">
                    {entry.mana_cost > 0 && (
                      <div className="mtg-mana-gem">
                        <span>{entry.mana_cost}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Art Frame */}
                <div className="mtg-art-frame relative">
                  {entry.image_url ? (
                    <img src={resolveImageUrl(entry.image_url, BACKEND_URL)} alt={entry.name} className="mtg-art-image" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="mtg-no-art">
                      <Sparkles size={32} style={{ color }} />
                    </div>
                  )}
                  {!entry.currently_owned && (
                    <div className="absolute inset-0 bg-black/45 flex items-end justify-center pb-1 z-20">
                      <span className="text-[8px] font-heading uppercase text-red-300 bg-red-500/40 px-1.5 py-0.5 rounded-full border border-red-500/50">
                        {isIt ? "persa" : "lost"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Type Line */}
                <div className="mtg-type-line">
                  <span className="mtg-type-text">
                    {t(lang, `type_${entry.type || "ability"}`)}
                  </span>
                  <div className="mtg-type-right">
                    {entry.rarity !== "common" && (
                      <span className="mtg-rarity-indicator font-heading" style={{ color }} title={t(lang, `rarity_${entry.rarity}`)}>
                        ✦
                      </span>
                    )}
                  </div>
                </div>

                {/* Rules / Text Box */}
                <div className="mtg-rules-box">
                  <div className="mtg-description">
                    {entry.effect && entry.effect !== "none" && entry.effect_value > 0 && (
                      <div className="mb-1 font-heading text-[9px] uppercase tracking-wider">
                        {entry.effect === "damage" && <span className="text-red-600">DANNO: {entry.effect_value}</span>}
                        {entry.effect === "heal" && <span className="text-green-600">CURA: {entry.effect_value}</span>}
                        {entry.effect === "buff" && <span className="text-amber-600">POTENZIAMENTO: +{entry.effect_value}</span>}
                        {entry.effect === "debuff" && <span className="text-purple-600">DEBUFF: -{entry.effect_value}</span>}
                      </div>
                    )}
                    <p className="leading-snug">{entry.description}</p>
                  </div>

                  {/* Stat Plaque */}
                  {(entry.type === "ally" || (entry.type === "object" && (entry.attack > 0 || entry.defense > 0))) && (
                    <div className="mtg-stat-plaque">
                      {entry.type === "ally" ? (
                        <>
                          <span>{entry.attack}</span> / <span className="text-emerald-700">{entry.hp}</span>
                        </>
                      ) : (
                        <>
                          {entry.attack > 0 && <span>+{entry.attack}⚔</span>}
                          {entry.attack > 0 && entry.defense > 0 && <span>/</span>}
                          {entry.defense > 0 && <span className="text-blue-700">+{entry.defense}🛡</span>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons Panel underneath the MTG card */}
            <div className="bg-[#111115]/95 border border-white/10 rounded-lg p-2.5 flex flex-col gap-2">
              {entry.lost_at && (
                <p className="text-[10px] text-red-400 bg-red-950/20 border border-red-900/30 px-2 py-1 rounded text-center font-heading">
                  ⚠️ {isIt ? "Persa il" : "Lost on"}: {new Date(entry.lost_at).toLocaleDateString()}
                  {entry.lost_reason && ` (${entry.lost_reason})`}
                </p>
              )}
              {entry.currently_owned && onTrade && (
                <button
                  onClick={() => { onTrade(entry.id, tradeCategory); onClose?.(); }}
                  data-testid={`trade-from-collection-${entry.id}`}
                  className="w-full bg-[#cfb53b]/10 hover:bg-[#cfb53b]/20 border border-[#cfb53b]/30 text-[#cfb53b] font-heading text-xs rounded-lg py-2 flex items-center justify-center gap-2 transition-all"
                >
                  <Handshake size={14} />
                  {isIt ? "Scambia questa carta" : "Trade this card"}
                </button>
              )}
              {canTranslate && (
                <button
                  onClick={handleTranslate}
                  disabled={translating}
                  data-testid={`translate-card-${entry.id}`}
                  className="w-full bg-[#0a0a0c] hover:bg-[#1a1a1d] border border-[#cfb53b]/30 hover:border-[#cfb53b]/60 text-[#cfb53b] font-heading text-xs rounded-lg py-2 flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                >
                  {translating ? <Loader2 size={14} className="animate-spin" /> : <Languages size={14} />}
                  {translating
                    ? (isIt ? "Traduzione in corso..." : "Translating...")
                    : (isIt ? "Traduci nella mia lingua" : "Translate to my language")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Collection({ onTradeCard }) {
  const { game } = useGame();
  const { token, API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  useTabMusic('collection');

  // Filter state
  const [search, setSearch] = useState("");
  const [rarityFilter, setRarityFilter] = useState("all");
  const [foilFilter, setFoilFilter] = useState("all"); // all | foil | non-foil
  const [ownedFilter, setOwnedFilter] = useState("all"); // all | owned | lost
  const [minAtk, setMinAtk] = useState("");
  const [minDef, setMinDef] = useState("");
  const [maxMana, setMaxMana] = useState("");
  // Iter 105 — PvE deck builder modal trigger
  const [showDeckBuilder, setShowDeckBuilder] = useState(false);

  const lang = game?.language || "en";
  const isIt = lang.startsWith("it");

  const load = useCallback(async () => {
    if (!game?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/game/${game.id}/collection`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load collection");
    } finally {
      setLoading(false);
    }
  }, [API, token, game?.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data?.entries) return [];
    const q = search.trim().toLowerCase();
    return data.entries.filter(e => {
      if (q && !(e.name || "").toLowerCase().includes(q)) return false;
      if (rarityFilter !== "all" && e.rarity !== rarityFilter) return false;
      if (foilFilter === "foil" && !e.foil) return false;
      if (foilFilter === "non-foil" && e.foil) return false;
      if (ownedFilter === "owned" && !e.currently_owned) return false;
      if (ownedFilter === "lost" && e.currently_owned) return false;
      if (minAtk !== "" && (e.attack || 0) < Number(minAtk)) return false;
      if (minDef !== "" && (e.defense || 0) < Number(minDef)) return false;
      if (maxMana !== "" && (e.mana_cost || 0) > Number(maxMana)) return false;
      return true;
    });
  }, [data, search, rarityFilter, foilFilter, ownedFilter, minAtk, minDef, maxMana]);

  const resetFilters = () => {
    setSearch("");
    setRarityFilter("all");
    setFoilFilter("all");
    setOwnedFilter("all");
    setMinAtk(""); setMinDef(""); setMaxMana("");
  };

  if (!game) return null;

  return (
    <div className="py-3 sm:py-6 px-2 sm:px-4" data-testid="collection-view">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 sm:mb-4">
          <h2 className="font-heading text-base sm:text-xl text-[#cfb53b] flex items-center gap-2">
            <Library size={18} /> {isIt ? "Libro della Collezione" : "Collection Book"}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Iter 105 — PvE Battle Deck button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeckBuilder(true)}
              data-testid="collection-deck-button"
              className="h-8 text-xs border-[#cfb53b]/40 text-[#cfb53b] hover:bg-[#cfb53b]/10"
            >
              <Swords size={14} className="mr-1.5" />
              {isIt ? "Deck Battaglia" : "Battle Deck"}
            </Button>
            {data && (
              <div className="flex items-center gap-2 text-[10px] sm:text-xs">
                <span className="bg-green-500/10 text-green-400 px-2 py-1 rounded-full font-heading" data-testid="collection-owned-count">
                  {isIt ? "Possedute" : "Owned"}: {data.owned}
                </span>
                <span className="bg-red-500/10 text-red-400 px-2 py-1 rounded-full font-heading">
                  {isIt ? "Perse" : "Lost"}: {data.lost}
                </span>
                <span className="bg-[#cfb53b]/10 text-[#cfb53b] px-2 py-1 rounded-full font-heading">
                  {isIt ? "Totale" : "Total"}: {data.total}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Rarity summary bar */}
        {data && data.total > 0 && (
          <div className="grid grid-cols-5 gap-1 mb-3 sm:mb-4">
            {RARITIES.slice().reverse().map(r => {
              const s = data.by_rarity[r] || { total: 0, owned: 0, foil: 0 };
              return (
                <div key={r} className="bg-[#0a0a0c] border rounded px-1.5 py-1 text-center" style={{ borderColor: `${RARITY_COLOR[r]}55` }} data-testid={`rarity-summary-${r}`}>
                  <p className="text-[9px] font-heading uppercase" style={{ color: RARITY_COLOR[r] }}>{t(lang, `rarity_${r}`)}</p>
                  <p className="font-heading text-xs sm:text-sm text-[#e2e8f0]">{s.owned}<span className="text-[#64748b]">/{s.total}</span></p>
                  {s.foil > 0 && <p className="text-[9px] text-[#fef08a]">✨ {s.foil}</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-2.5 sm:p-3 mb-3 space-y-2" data-testid="collection-filters">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#64748b]" />
            <Input
              data-testid="collection-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isIt ? "Cerca per nome..." : "Search by name..."}
              className="pl-8 h-8 text-xs bg-[#0a0a0c] border-[#2d3748] focus:border-[#cfb53b]/40"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {["all", ...RARITIES].map(r => (
              <button
                key={r}
                data-testid={`rarity-filter-${r}`}
                onClick={() => setRarityFilter(r)}
                className={`text-[10px] font-heading uppercase px-2 py-0.5 rounded-full transition-colors ${
                  rarityFilter === r
                    ? "bg-[#cfb53b]/25 text-[#cfb53b] border border-[#cfb53b]/50"
                    : "bg-[#0a0a0c] text-[#94a3b8] border border-[#2d3748] hover:border-[#cfb53b]/30"
                }`}
                style={rarityFilter === r && r !== "all" ? { color: RARITY_COLOR[r], borderColor: RARITY_COLOR[r] + "66" } : {}}
              >
                {r === "all" ? (isIt ? "Tutte" : "All") : t(lang, `rarity_${r}`)}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
            <select
              data-testid="foil-filter"
              value={foilFilter}
              onChange={(e) => setFoilFilter(e.target.value)}
              className="bg-[#0a0a0c] border border-[#2d3748] rounded px-2 py-1 text-[11px] text-[#e2e8f0]"
            >
              {React.createElement("option", { value: "all" }, isIt ? "Foil: tutte" : "Foil: all")}
              {React.createElement("option", { value: "foil" }, isIt ? "Solo Foil ✨" : "Foil only ✨")}
              {React.createElement("option", { value: "non-foil" }, isIt ? "No Foil" : "No foil")}
            </select>
            <select
              data-testid="owned-filter"
              value={ownedFilter}
              onChange={(e) => setOwnedFilter(e.target.value)}
              className="bg-[#0a0a0c] border border-[#2d3748] rounded px-2 py-1 text-[11px] text-[#e2e8f0]"
            >
              {React.createElement("option", { value: "all" }, isIt ? "Possesso: tutte" : "Ownership: all")}
              {React.createElement("option", { value: "owned" }, isIt ? "Possedute" : "Owned")}
              {React.createElement("option", { value: "lost" }, isIt ? "Perse" : "Lost")}
            </select>
            <input
              data-testid="filter-min-atk"
              type="number" min={0} placeholder={isIt ? "ATT min" : "Min ATK"}
              value={minAtk} onChange={(e) => setMinAtk(e.target.value)}
              className="bg-[#0a0a0c] border border-[#2d3748] rounded px-2 py-1 text-[11px] text-[#e2e8f0]"
            />
            <input
              data-testid="filter-min-def"
              type="number" min={0} placeholder={isIt ? "DIF min" : "Min DEF"}
              value={minDef} onChange={(e) => setMinDef(e.target.value)}
              className="bg-[#0a0a0c] border border-[#2d3748] rounded px-2 py-1 text-[11px] text-[#e2e8f0]"
            />
            <input
              data-testid="filter-max-mana"
              type="number" min={0} placeholder={isIt ? "Costo max" : "Max mana"}
              value={maxMana} onChange={(e) => setMaxMana(e.target.value)}
              className="bg-[#0a0a0c] border border-[#2d3748] rounded px-2 py-1 text-[11px] text-[#e2e8f0]"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-[#64748b]" data-testid="filter-count">
              {filtered.length} / {data?.total || 0} {isIt ? "risultati" : "results"}
            </span>
            <Button size="sm" variant="ghost" onClick={resetFilters} className="h-6 text-[10px] text-[#94a3b8] hover:text-[#cfb53b]" data-testid="reset-filters">
              {isIt ? "Reset" : "Reset"}
            </Button>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-[#cfb53b]" /></div>
        ) : error ? (
          <div className="text-center py-12 text-red-400">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-[#2d3748] rounded-lg">
            <Library size={36} className="mx-auto text-[#2d3748] mb-2" />
            <p className="text-[#94a3b8] text-sm">
              {data?.total === 0
                ? (isIt ? "La tua collezione è vuota. Trova o scambia carte per riempirla!" : "Your collection is empty. Find or trade cards to fill it!")
                : (isIt ? "Nessuna carta corrisponde ai filtri." : "No cards match the filters.")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
            {filtered.map((e) => {
              const color = RARITY_COLOR[e.rarity] || "#94a3b8";
              const typeClass = e.type === "ally" ? "card-ally" : e.type === "ability" ? "card-ability" : "card-object";
              const rarityClass = e.rarity ? `rarity-${e.rarity.toLowerCase()}` : "rarity-common";
              const hasPlaque = e.type === "ally" || (e.type === "object" && ((e.attack || 0) > 0 || (e.defense || 0) > 0));

              return (
                <button
                  key={e.id}
                  data-testid={`collection-card-${e.id}`}
                  onClick={() => setSelected(e)}
                  className="group relative text-left transition-all hover:scale-105"
                >
                  <div
                    className={`mtg-card ${typeClass} ${rarityClass} variant-compact ${
                      e.currently_owned ? "" : "grayscale-[55%] opacity-75"
                    }`}
                  >
                    <div className={`mtg-inner-frame ${e.foil ? "card-foil" : ""}`}>
                      {/* Header bar: Title + Mana */}
                      <div className="mtg-header-bar">
                        <span className="mtg-card-title">{e.name}</span>
                        <div className="mtg-header-right">
                          {e.level > 1 && (
                            <span className="mtg-level-badge">Lv{e.level}</span>
                          )}
                          {e.mana_cost > 0 && (
                            <div className="mtg-mana-gem">
                              <span>{e.mana_cost}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Artwork */}
                      <div className="mtg-art-frame">
                        {e.image_url ? (
                          <img
                            src={resolveImageUrl(e.image_url, BACKEND_URL)}
                            alt={e.name}
                            className="mtg-art-image"
                            loading="lazy"
                          />
                        ) : (
                          <div className="mtg-no-art">
                            <Sparkles size={16} style={{ color }} />
                          </div>
                        )}
                        {!e.currently_owned && (
                          <div className="absolute inset-0 bg-black/45 flex items-end justify-center pb-1 z-20">
                            <span className="text-[8px] font-heading uppercase text-red-300 bg-red-500/40 px-1.5 py-0.5 rounded-full border border-red-500/50">
                              {isIt ? "persa" : "lost"}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Stat plaque */}
                      {hasPlaque && (
                        <div className="mtg-stat-plaque">
                          {e.type === "ally" ? (
                            <>
                              <span>{e.attack}</span>/<span>{e.hp}</span>
                            </>
                          ) : (
                            <>
                              {e.attack > 0 && <span>+{e.attack}</span>}
                              {e.attack > 0 && e.defense > 0 && <span>/</span>}
                              {e.defense > 0 && <span>+{e.defense}</span>}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <CardModal
        entry={selected}
        lang={lang}
        onClose={() => setSelected(null)}
        onTrade={onTradeCard}
        onTranslated={(updatedCard) => {
          // Refresh the collection so the list shows the translated name too
          load();
          setSelected((prev) => prev ? { ...prev, name: updatedCard.name, description: updatedCard.description, _translations: updatedCard._translations } : prev);
        }}
      />
      {/* Iter 105 — PvE Battle Deck builder */}
      {showDeckBuilder && (
        <PveDeckBuilder onClose={() => setShowDeckBuilder(false)} />
      )}
    </div>
  );
}
